import L from 'leaflet';
import React, { useEffect, useRef, useState } from 'react';
import { MapContainer, TileLayer, Marker, useMap } from 'react-leaflet';

type MapMarker = {
  icon: L.Icon;
  entities: [number, number][];
}

interface MapProps {
  coordinates: number[];
  className?: string;
  markers: MapMarker[];
}

function SmoothZoomToBounds({ bounds, animate, callback }: any) {
  const map = useMap();
  const previousBoundsRef = useRef(null);

  useEffect(() => {
    const previousBounds = previousBoundsRef.current;

    if (previousBounds && bounds !== previousBounds) {
      map
        .flyToBounds(bounds, {
          animate: animate || false,
          duration: 2,
          easeLinearity: 0.99,
        })
        .on('moveend', () => {
          const zoomLevel = map.getZoom();

          // If there's a callback provided, call it
          if (callback) {
            callback(zoomLevel);
          }
        });
    }

    previousBoundsRef.current = bounds;
  }, [bounds, animate, map, callback]);

  return null;
}

const InteractiveMap: React.FC<MapProps> = ({
  coordinates,
  className,
  markers,
}) => {
  const [bounds, setBounds] = useState<L.LatLngBounds | null>(null);
  const [zoom, setZoom] = useState<any>(6);

  useEffect(() => {
    if (markers && markers.length > 0) { 
      const allCoords = markers.flatMap(marker => {
        return marker.entities;
      })
      boundToItems(allCoords);
    }
  }, [markers]);

  const position: [number, number] = [coordinates[1], coordinates[0]];

  const MapInvalidateSize = () => {
    const map = useMap();
    useEffect(() => {
      map.invalidateSize();
    }, [map]);
    return null;
  };

  const setAndRenderBounds = (bounds: L.LatLngBounds) => {
    setBounds(bounds);
  };

  const callback = (newZoom: number) => {
    setZoom(newZoom);
  };

  const boundToItems = async (items: any) => {
    if (items && items.length > 0) {
      const bounds = L.latLngBounds(items);
      setAndRenderBounds(bounds);
    }
  };

  return (
    <div className={`${className} h-full w-full`}>
      <MapContainer
        center={position}
        zoom={zoom}
        scrollWheelZoom={true}
        className="h-full w-full"
        zoomControl={false}
      >
        <SmoothZoomToBounds  
          bounds={bounds}
          animate={true}
          callback={callback}
        />
        <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
        <MapInvalidateSize />
        
        {markers?.map((marker: MapMarker, i: any) => 
          marker.entities.map((coords, j: any) => (
            <Marker key={`${coords}-${i}-${j}`} icon={marker.icon} position={coords}/>
          ))
        )}
      </MapContainer>
    </div>
  );
};

export default InteractiveMap;
